
// src/components/EmblaCarousel.js

import React, { useState, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import PropTypes from 'prop-types';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import './EmblaCarousel.css'; // Asegúrate de tener este archivo

/**
 * Carrusel reutilizable basado en Embla.
 *
 * Props:
 * - photos: Array de { url_imagen: string, alt?: string }.
 * - heightDesktop: altura en escritorio (p.ej. "400px").
 * - heightMobile: altura en móvil (p.ej. "200px").
 * - showArrows, showDots, loop, inViewThreshold.
 */
const EmblaCarousel = ({
  photos,
  heightDesktop = '400px',
  heightMobile = '200px',
  showArrows = true,
  showDots = true,
  loop = false,
  inViewThreshold = 0.2,
}) => {
  // Estado para el índice activo
  const [currentIndex, setCurrentIndex] = useState(0);
  // Estado dinámico de altura según viewport
  const [heightState, setHeightState] = useState(heightDesktop);

  // Media query: detecta ancho <= 767px (mobile)
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const updateHeight = (e) => {
      setHeightState(e.matches ? heightMobile : heightDesktop);
    };
    // Inicializa
    updateHeight(mql);
    // Escucha cambios
    mql.addEventListener('change', updateHeight);
    return () => mql.removeEventListener('change', updateHeight);
  }, [heightDesktop, heightMobile]);

  // Embla hook
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop, inViewThreshold });

  // Sincroniza índice al cambiar de slide
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setCurrentIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    onSelect();
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi]);

  return (
    <div className="embla-carousel">
      <div
        className="embla-carousel__wrapper"
        style={{ height: heightState }}
      >
        <div className="embla-carousel__viewport embla" ref={emblaRef}>
          <div className="embla-carousel__container embla__container">
            {photos && photos.length > 0 ? (
              photos.map((photo, idx) => (
                <div className="embla-carousel__slide embla__slide" key={idx}>
                  <img
                    src={photo.url_imagen}
                    alt={photo.alt || `Imagen ${idx + 1}`}
                    className="embla-carousel__slide-img embla__slide__img"
                  />
                </div>
              ))
            ) : (
              <div className="embla-carousel__no-photo">No hay fotos</div>
            )}
          </div>
        </div>

        {showArrows && photos && photos.length > 1 && emblaApi && (
          <div className="embla-carousel__arrows carousel-controls">
            <button
              className="carousel-button prev"
              onClick={() => emblaApi.scrollPrev()}
            >
              <FaArrowLeft />
            </button>
            <button
              className="carousel-button next"
              onClick={() => emblaApi.scrollNext()}
            >
              <FaArrowRight />
            </button>
          </div>
        )}
      </div>

      {showDots && photos && photos.length > 0 && (
        <div className="embla-carousel__dots carousel-dots">
          {photos.map((_, idx) => (
            <span
              key={idx}
              className={`dot ${idx === currentIndex ? 'active' : ''}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}; // >>> Cierre correcto de la función EmblaCarousel

// >>> PropTypes y export deben estar en el nivel superior
EmblaCarousel.propTypes = {
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      url_imagen: PropTypes.string.isRequired,
      alt: PropTypes.string,
    })
  ),
  heightDesktop: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  heightMobile: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showArrows: PropTypes.bool,
  showDots: PropTypes.bool,
  loop: PropTypes.bool,
  inViewThreshold: PropTypes.number,
};

export default EmblaCarousel;
